/**
 * Schema-driven project date fields (edit view) — the heart of the editable dates panel.
 *
 * Rather than hand-coding a DatePicker per date, this renders whatever fields the *date schema*
 * describes, so adding or removing a project date becomes a schema change (backend) instead of a code
 * change here. The read-only counterpart shown on the project overview is DatesSection.tsx.
 *
 * Data flow on each render:
 *   1. `useEditDatesPanel` supplies the schema: either the project's parsed standard-specific schema,
 *      or FALLBACK_PROJECT_DATES_SCHEMA when the project has none (see that file for why).
 *   2. Each phase section (Feasibility / Design / …) passes its schema `properties` in as one or more
 *      `groups`, together with the current `dates` values for that group.
 *   3. Here every group's properties are flattened into `keyPrefix.fieldKey` entries — skipping
 *      structural keys (STRUCTURAL_DATE_KEYS) and any non-object definition (isRenderableFieldDefinition)
 *      — then merged with frontend presentation metadata (label / tooltip / order, resolved
 *      schema-first by resolveDateFieldMetadata; see projectDateFieldMetadata.ts).
 *   4. The generic DynamicFormFieldsFromJsonSchema widget renders those entries as inputs and reports
 *      edits back keyed by the flattened path, which the panel writes into `dates`.
 */
import { getDateFieldLabel, getDateFieldMetadata, STRUCTURAL_DATE_KEYS } from "../../../../constants";
import { convertDateToStringDDMMYYYY, getDateFromValue } from "../../../../utils";
import {
  DynamicFieldValueMappers,
  DynamicFormFieldsFromJsonSchema,
  DynamicJsonSchemaProperty,
  isDateField,
  isRenderableFieldDefinition,
  normaliseSchemaFieldLabel,
  renderFieldsInTwoColumnRows,
} from "../../../../widget";

/** A single date schema group (e.g. implementation, duration) rendered by DateSchemaFields. */
export interface DateSchemaGroup {
  /** The `properties` object for this group from the parsed date schema (e.g. schema.feasibility.properties). */
  schemaProperties: unknown;
  /** The current values for this group (e.g. dates.feasibility). */
  values: unknown;
  /** Flattened-path prefix for the group, used for metadata lookup and building change keys (e.g. "feasibility"). */
  keyPrefix: string;
  /** Field keys to hide in addition to the always-hidden structural keys. */
  excludeKeys?: string[];
}

interface DateSchemaFieldsProps {
  /**
   * One or more schema groups rendered together in a single two-column grid. Groups render in
   * array order and fields within a group keep their metadata sequence. Provide either `groups`
   * or the single-group `schemaProperties`/`values`/`keyPrefix` props.
   */
  groups?: DateSchemaGroup[];
  /** The `properties` object for a single group (e.g. schema.feasibility.properties). */
  schemaProperties?: unknown;
  /** The current values for a single group (e.g. dates.feasibility). */
  values?: unknown;
  /** Flattened-path prefix for a single group (e.g. "feasibility"). */
  keyPrefix?: string;
  /** Field keys to hide in addition to the always-hidden structural keys (single-group API). */
  excludeKeys?: string[];
  isReadonly?: boolean;
  hasError?: (flattenedKey: string) => boolean;
  onChange?: (value: Date | number | undefined, flattenedKey: string) => void;
}

/** Sequence gap reserved per group so earlier groups always render before later ones. */
const GROUP_SEQUENCE_STRIDE = 1000;

/** Sequence given to fields without configured metadata so they sort after sequenced fields in their group. */
const UNSEQUENCED_FIELD_ORDER = 999;

/**
 * When a phase end date changes, the following phase's start date is auto-filled
 * to the day after, mirroring the previous hand-rolled section behaviour.
 */
const PHASE_END_TO_NEXT_START_CASCADE: Record<string, string> = {
  "feasibility.end": "design.start",
  "design.end": "implementation.start",
  "implementation.end": "ongoingMonitoring.overallStart",
};

const dateFieldValueMappers: DynamicFieldValueMappers<DynamicJsonSchemaProperty> = {
  date: ({ value }) => value ?? undefined,
  number: ({ value }) => {
    if (value === "" || value === null || value === undefined) {
      return undefined;
    }
    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? undefined : parsedValue;
  },
};

const formatDateReadonlyValue = ({
  fieldDefinition,
  fieldValue,
}: {
  fieldDefinition: DynamicJsonSchemaProperty;
  fieldValue: unknown;
}): string | undefined => {
  if (!isDateField(fieldDefinition)) {
    return undefined;
  }

  const date = getDateFromValue(fieldValue);
  return date ? convertDateToStringDDMMYYYY(date) : undefined;
};

const toRecord = <TValue,>(value: unknown): Record<string, TValue> | undefined => {
  if (!value || typeof value !== "object") {
    return undefined;
  }

  return value as Record<string, TValue>;
};

/**
 * Resolves the presentation metadata (label, tooltip, ordering) for a single date field.
 *
 * Precedence is **schema-first**: any value carried on the schema property itself wins, and the
 * frontend metadata store ({@link getDateFieldMetadata}, see projectDateFieldMetadata.ts) is only a
 * fallback. This is the single place date-field metadata is sourced, so migrating to a fully
 * schema-driven approach is just a matter of dropping the `feMetadata` fallbacks below (and
 * eventually deleting projectDateFieldMetadata.ts) once the backend schema carries `title`,
 * `tooltip` and `sequence` on each property.
 */
const resolveDateFieldMetadata = (
  keyPrefix: string,
  fieldKey: string,
  fieldDefinition: DynamicJsonSchemaProperty
): { label: string; tooltip?: string; sequence?: number } => {
  const feMetadata = getDateFieldMetadata(keyPrefix, fieldKey);

  return {
    label: fieldDefinition.title ?? getDateFieldLabel(keyPrefix, fieldKey),
    tooltip: fieldDefinition.tooltip ?? feMetadata?.tooltip,
    sequence: fieldDefinition.sequence ?? feMetadata?.sequence,
  };
};

/**
 * Renders one or more groups of project date fields directly from the date schema, merging
 * in frontend presentation metadata (label, tooltip, ordering). Multiple groups share a single
 * two-column grid so their fields flow together rather than starting a new row per group. Any
 * field present in the schema is shown; absent fields are not.
 *
 * Field identity is the flattened path (`keyPrefix.fieldKey`), which is what error lookups and
 * change handlers receive so fields from different groups never collide.
 */
export const DateSchemaFields = ({
  groups,
  schemaProperties,
  values,
  keyPrefix,
  isReadonly = false,
  excludeKeys,
  hasError,
  onChange,
}: DateSchemaFieldsProps): JSX.Element | null => {
  const resolvedGroups: DateSchemaGroup[] =
    groups ?? (keyPrefix !== undefined ? [{ schemaProperties, values, keyPrefix, excludeKeys }] : []);

  const entries: [string, DynamicJsonSchemaProperty][] = [];
  const valueByKey: Record<string, unknown> = {};
  const labelByKey: Record<string, string> = {};

  resolvedGroups.forEach((group, groupIndex) => {
    const properties = toRecord<DynamicJsonSchemaProperty>(group.schemaProperties);
    if (!properties) {
      return;
    }

    const valueRecord = toRecord<unknown>(group.values);
    const excludedKeys = new Set(group.excludeKeys ?? []);
    const isHidden = (fieldKey: string): boolean => STRUCTURAL_DATE_KEYS.has(fieldKey) || excludedKeys.has(fieldKey);
    const groupSequenceBase = groupIndex * GROUP_SEQUENCE_STRIDE;

    Object.entries(properties)
      .filter(([fieldKey, fieldDefinition]) => isRenderableFieldDefinition(fieldDefinition) && !isHidden(fieldKey))
      .forEach(([fieldKey, fieldDefinition]) => {
        const flattenedKey = `${group.keyPrefix}.${fieldKey}`;
        const { label, tooltip, sequence } = resolveDateFieldMetadata(group.keyPrefix, fieldKey, fieldDefinition);

        entries.push([
          flattenedKey,
          {
            ...fieldDefinition,
            tooltip,
            // Offset by the group's base so earlier groups always render before later ones,
            // while fields within a group keep their own relative ordering.
            sequence: groupSequenceBase + (sequence ?? UNSEQUENCED_FIELD_ORDER),
          },
        ]);
        valueByKey[flattenedKey] = valueRecord?.[fieldKey];
        labelByKey[flattenedKey] = label;
      });
  });

  if (entries.length === 0) {
    return null;
  }

  const handleFieldChange = (flattenedKey: string, value: unknown): void => {
    onChange?.(value as Date | number | undefined, flattenedKey);

    const cascadeTargetKey = PHASE_END_TO_NEXT_START_CASCADE[flattenedKey];
    if (cascadeTargetKey) {
      const cascadeValue = value instanceof Date ? new Date(new Date(value).setDate(value.getDate() + 1)) : undefined;
      onChange?.(cascadeValue, cascadeTargetKey);
    }
  };

  return (
    <DynamicFormFieldsFromJsonSchema
      schemaProperties={entries}
      isReadonly={isReadonly}
      getFieldValue={(flattenedKey) => valueByKey[flattenedKey]}
      onFieldChange={isReadonly ? undefined : handleFieldChange}
      hasFieldError={(flattenedKey) => hasError?.(flattenedKey) ?? false}
      getFieldLabel={(flattenedKey) => labelByKey[flattenedKey] ?? normaliseSchemaFieldLabel(flattenedKey)}
      valueMappers={dateFieldValueMappers}
      formatReadonlyValue={formatDateReadonlyValue}
      renderLayout={(fields) => <>{renderFieldsInTwoColumnRows(fields)}</>}
    />
  );
};
